import { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Shield, Brain, Scale, ChevronRight, ArrowUpRight, Send, Globe, BarChart3, FileSearch } from "lucide-react";

const GOLD = "#D4AF37";
const BG = "#05070A";
const SURFACE = "#0A0D14";

// ══════════════════ TRANSLATIONS ══════════════════
const T = {
  es: {
    nav: { servicios: "Servicios", nosotros: "Nosotros", contacto: "Contacto", acceder: "ACCEDER" },
    hero: {
      subtitle: "LEGAL INTELLIGENT ADVISOR",
      title1: "Inteligencia Legal",
      title2: "de Siguiente Nivel",
      desc: "Coordinamos y auditamos las operaciones legales mas complejas del mundo. Tecnologia con IA para corporaciones globales y organizaciones con alta complejidad legal.",
      cta1: "ACCEDER A LA PLATAFORMA",
      cta2: "NUESTROS SERVICIOS",
      stats: [["Global", "Multi-Jurisdiccional"], ["IA", "Inteligencia Artificial"], ["24/7", "Monitoreo Continuo"]]
    },
    marquee: ["MULTI-JURISDICCIONAL", "AUDITORIA IA", "INTELIGENCIA LEGAL", "CONTROL TOTAL", "TECNOLOGIA AVANZADA", "COORDINACION GLOBAL"],
    services: {
      label: "SERVICIOS",
      title: "Lo que hacemos",
      items: [
        { title: "Coordinacion Legal Multi-Jurisdiccional", desc: "Gestionamos portafolios legales complejos con multiples escritorios juridicos, tribunales internacionales y actores institucionales. Control centralizado de cada caso, firma asignada y conflicto de intereses." },
        { title: "Auditoria IA de Escritorios Juridicos", desc: "Inteligencia artificial que audita facturas, monitorea horas facturadas y compara honorarios entre firmas. Deteccion automatica de anomalias y cobros fuera de contrato." },
        { title: "Plataforma de Inteligencia Legal", desc: "Tecnologia propietaria que consolida datos de todos los casos, genera briefings ejecutivos con IA, y proporciona dashboards en tiempo real para la toma de decisiones estrategicas a nivel ejecutivo." }
      ]
    },
    capabilities: {
      label: "CAPACIDADES",
      title1: "Tecnologia que ",
      title2: "transforma",
      items: [
        { label: "Conflictos de Intereses", desc: "Deteccion y gestion automatica" },
        { label: "Analisis Documental IA", desc: "Resumenes juridicos instantaneos" },
        { label: "Acceso Basado en Roles", desc: "Seguridad por nivel y caso" },
        { label: "Dashboards en Tiempo Real", desc: "Metricas de exposicion y riesgo" }
      ]
    },
    about: {
      label: "SOBRE MIDAS",
      title1: "Donde la estrategia legal",
      title2: "se encuentra con la tecnologia",
      p1: "Midas es una firma de servicios legales inteligentes que combina experiencia en litigios internacionales con tecnologia de inteligencia artificial de vanguardia.",
      p2: "Nuestra plataforma permite a corporaciones globales y organizaciones con multiples jurisdicciones coordinar decenas de escritorios juridicos, auditar millones en honorarios legales, y tomar decisiones estrategicas respaldadas por datos en tiempo real.",
      p3: "Operamos en las jurisdicciones mas complejas del mundo, donde un error puede costar miles de millones y la coordinacion entre firmas es la diferencia entre el exito y el fracaso.",
      stats: [["Global", "Cobertura Internacional"], ["360°", "Control Total"]]
    },
    contact: {
      label: "CONTACTO",
      title: "Hablemos",
      desc: "Cuentenos sobre su caso y le mostraremos como Midas puede ayudarle.",
      nombre: "Nombre",
      email: "Email",
      empresa: "Empresa / Organizacion",
      mensaje: "Describa brevemente su situacion legal...",
      btn: "ENVIAR MENSAJE",
      sent: "MENSAJE ENVIADO"
    },
    footer: "Todos los derechos reservados. Confidencial."
  },
  en: {
    nav: { servicios: "Services", nosotros: "About", contacto: "Contact", acceder: "LOGIN" },
    hero: {
      subtitle: "LEGAL INTELLIGENT ADVISOR",
      title1: "Legal Intelligence",
      title2: "Next Level",
      desc: "We coordinate and audit the most complex legal operations in the world. AI-powered technology for global corporations and organizations with high legal complexity.",
      cta1: "ACCESS PLATFORM",
      cta2: "OUR SERVICES",
      stats: [["Global", "Multi-Jurisdictional"], ["AI", "Artificial Intelligence"], ["24/7", "Continuous Monitoring"]]
    },
    marquee: ["MULTI-JURISDICTIONAL", "AI AUDITING", "LEGAL INTELLIGENCE", "TOTAL CONTROL", "ADVANCED TECHNOLOGY", "GLOBAL COORDINATION"],
    services: {
      label: "SERVICES",
      title: "What we do",
      items: [
        { title: "Multi-Jurisdictional Legal Coordination", desc: "We manage complex legal portfolios with multiple law firms, international tribunals, and institutional actors. Centralized control of each case, assigned firm, and conflict of interest." },
        { title: "AI Auditing of Law Firms", desc: "Artificial intelligence that audits invoices, monitors billed hours, and compares fees across firms. Automatic detection of anomalies and out-of-contract charges." },
        { title: "Legal Intelligence Platform", desc: "Proprietary technology that consolidates data from all cases, generates AI-powered executive briefings, and provides real-time dashboards for strategic decision-making at the executive level." }
      ]
    },
    capabilities: {
      label: "CAPABILITIES",
      title1: "Technology that ",
      title2: "transforms",
      items: [
        { label: "Conflict of Interest", desc: "Automatic detection & management" },
        { label: "AI Document Analysis", desc: "Instant legal summaries" },
        { label: "Role-Based Access", desc: "Security by level and case" },
        { label: "Real-Time Dashboards", desc: "Exposure and risk metrics" }
      ]
    },
    about: {
      label: "ABOUT MIDAS",
      title1: "Where legal strategy",
      title2: "meets technology",
      p1: "Midas is an intelligent legal services firm that combines international litigation expertise with cutting-edge artificial intelligence technology.",
      p2: "Our platform enables global corporations and multi-jurisdictional organizations to coordinate dozens of law firms, audit millions in legal fees, and make strategic decisions backed by real-time data.",
      p3: "We operate in the most complex jurisdictions in the world, where a single mistake can cost billions and coordination between firms is the difference between success and failure.",
      stats: [["Global", "International Coverage"], ["360°", "Total Control"]]
    },
    contact: {
      label: "CONTACT",
      title: "Let's Talk",
      desc: "Tell us about your case and we'll show you how Midas can help.",
      nombre: "Name",
      email: "Email",
      empresa: "Company / Organization",
      mensaje: "Briefly describe your legal situation...",
      btn: "SEND MESSAGE",
      sent: "MESSAGE SENT"
    },
    footer: "All rights reserved. Confidential."
  }
};

const FadeIn = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

// ══════════════════ LANG TOGGLE ══════════════════
function LangToggle({ lang, setLang }) {
  return (
    <div data-testid="lang-toggle" className="flex items-center border border-white/15 overflow-hidden" style={{ borderRadius: 2 }}>
      <button onClick={() => setLang("es")} className="px-3 py-1 text-xs tracking-wider transition-all duration-200" style={{ background: lang === "es" ? GOLD : "transparent", color: lang === "es" ? "#000" : "rgba(255,255,255,0.4)", fontFamily: "'Outfit', sans-serif", fontWeight: 500, border: "none", cursor: "pointer" }}>ES</button>
      <button onClick={() => setLang("en")} className="px-3 py-1 text-xs tracking-wider transition-all duration-200" style={{ background: lang === "en" ? GOLD : "transparent", color: lang === "en" ? "#000" : "rgba(255,255,255,0.4)", fontFamily: "'Outfit', sans-serif", fontWeight: 500, border: "none", cursor: "pointer" }}>EN</button>
    </div>
  );
}

// ══════════════════ HEADER ══════════════════
function Header({ onAccess, lang, setLang }) {
  const t = T[lang].nav;
  return (
    <header className="fixed top-0 w-full z-50 border-b border-white/10" style={{ background: `${BG}cc`, backdropFilter: "blur(20px)" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border border-[#D4AF37] flex items-center justify-center">
            <span style={{ color: GOLD, fontSize: 14 }}>M</span>
          </div>
          <span data-testid="header-logo" className="font-serif text-xl tracking-[0.2em] text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>MIDAS</span>
        </div>
        <nav className="hidden md:flex items-center gap-8">
          <a href="#servicios" className="text-sm text-slate-400 hover:text-white transition-colors tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.servicios}</a>
          <a href="#nosotros" className="text-sm text-slate-400 hover:text-white transition-colors tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.nosotros}</a>
          <a href="#contacto" className="text-sm text-slate-400 hover:text-white transition-colors tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.contacto}</a>
          <LangToggle lang={lang} setLang={setLang} />
        </nav>
        <div className="flex items-center gap-3 md:hidden">
          <LangToggle lang={lang} setLang={setLang} />
        </div>
        <button data-testid="header-access-btn" onClick={onAccess} className="hidden md:flex items-center gap-2 px-5 py-2 text-sm tracking-wider transition-all duration-300 hover:brightness-110" style={{ background: GOLD, color: "#000", fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}>
          {t.acceder} <ChevronRight size={14} />
        </button>
      </div>
    </header>
  );
}

// ══════════════════ HERO ══════════════════
function Hero({ onAccess, lang }) {
  const t = T[lang].hero;
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ background: BG }}>
      <div className="absolute inset-0">
        <img src="https://images.unsplash.com/photo-1760553120264-98dd3d59fc02?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBkYXJrJTIwYWJzdHJhY3QlMjB0ZWNofGVufDB8fHx8MTc3NTM1MTgyM3ww&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${BG}66, ${BG}cc, ${BG})` }} />
      </div>
      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 text-center pt-20">
        <FadeIn>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase mb-6" style={{ color: GOLD, fontFamily: "'Outfit', sans-serif" }}>{t.subtitle}</p>
        </FadeIn>
        <FadeIn delay={0.15}>
          <h1 className="text-5xl sm:text-6xl lg:text-[5.5rem] font-medium tracking-tight leading-[0.95] text-white mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t.title1}<br /><span style={{ color: GOLD }}>{t.title2}</span>
          </h1>
        </FadeIn>
        <FadeIn delay={0.3}>
          <p className="text-base lg:text-lg font-light tracking-wide text-slate-300 max-w-2xl mx-auto mb-12 leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.desc}</p>
        </FadeIn>
        <FadeIn delay={0.45}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button data-testid="hero-access-btn" onClick={onAccess} className="flex items-center gap-3 px-8 py-4 text-sm tracking-[0.15em] font-medium transition-all duration-300 hover:brightness-110 hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]" style={{ background: GOLD, color: "#000", fontFamily: "'Outfit', sans-serif" }}>
              {t.cta1} <ArrowUpRight size={16} />
            </button>
            <a href="#servicios" className="flex items-center gap-2 px-8 py-4 text-sm tracking-[0.15em] font-medium border border-white/20 text-white hover:border-white/40 transition-all duration-300" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t.cta2}
            </a>
          </div>
        </FadeIn>
        <FadeIn delay={0.6}>
          <div className="mt-20 grid grid-cols-3 gap-8 max-w-lg mx-auto">
            {t.stats.map(([val, label], i) => (
              <div key={i} className="text-center">
                <div className="text-2xl lg:text-3xl font-medium text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{val}</div>
                <div className="text-[10px] tracking-[0.15em] uppercase text-slate-500 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{label}</div>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ══════════════════ MARQUEE ══════════════════
function Marquee({ lang }) {
  const words = T[lang].marquee;
  const doubled = [...words, ...words];
  return (
    <div className="border-y border-white/5 overflow-hidden py-6" style={{ background: SURFACE }}>
      <motion.div className="flex whitespace-nowrap" animate={{ x: ["0%", "-50%"] }} transition={{ duration: 30, repeat: Infinity, ease: "linear" }}>
        {doubled.map((w, i) => (
          <span key={i} className="text-xl lg:text-2xl tracking-[0.2em] uppercase mx-8 flex items-center gap-8" style={{ fontFamily: "'Cormorant Garamond', serif", color: "rgba(255,255,255,0.2)" }}>
            {w} <span style={{ color: GOLD, fontSize: 8 }}>◆</span>
          </span>
        ))}
      </motion.div>
    </div>
  );
}

// ══════════════════ SERVICES ══════════════════
function Services({ lang }) {
  const t = T[lang].services;
  const icons = [Globe, Brain, BarChart3];
  const images = [
    "https://images.unsplash.com/photo-1675015989087-562d52d254f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxnbG9iYWwlMjBuZXR3b3JrJTIwbGVnYWx8ZW58MHx8fHwxNzc1MzUxODIzfDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1770486036751-e55247238964?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxhaSUyMGRhdGElMjBhYnN0cmFjdCUyMGRhcmt8ZW58MHx8fHwxNzc1MzUxODIzfDA&ixlib=rb-4.1.0&q=85",
    null
  ];
  const spans = ["md:col-span-5", "md:col-span-3", "md:col-span-8"];

  return (
    <section id="servicios" className="py-24 lg:py-32" style={{ background: BG }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <FadeIn>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: GOLD, fontFamily: "'Outfit', sans-serif" }}>{t.label}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white mb-16" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t.title}</h2>
        </FadeIn>
        <div className="grid grid-cols-1 md:grid-cols-8 gap-6 lg:gap-8">
          {t.items.map((s, i) => {
            const Icon = icons[i];
            return (
              <FadeIn key={i} delay={i * 0.12} className={spans[i]}>
                <div data-testid={`service-card-${i}`} className="group relative overflow-hidden border border-white/5 hover:border-white/15 transition-all duration-500 h-full" style={{ background: SURFACE }}>
                  {images[i] && (
                    <div className="relative h-48 w-full overflow-hidden">
                      <img src={images[i]} alt={s.title} className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
                      <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${SURFACE}, transparent)` }} />
                    </div>
                  )}
                  <div className="p-8 md:p-10">
                    <Icon size={28} strokeWidth={1.5} style={{ color: GOLD }} className="mb-6" />
                    <h3 className="text-xl lg:text-2xl font-medium text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.desc}</p>
                  </div>
                  {i === 2 && (
                    <div className="absolute top-6 right-6">
                      <span className="text-[10px] tracking-[0.15em] uppercase px-3 py-1 border" style={{ color: GOLD, borderColor: `${GOLD}44`, fontFamily: "'Outfit', sans-serif" }}>CORE PLATFORM</span>
                    </div>
                  )}
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ══════════════════ CAPABILITIES ══════════════════
function Capabilities({ lang }) {
  const t = T[lang].capabilities;
  const capIcons = [Scale, FileSearch, Shield, BarChart3];
  return (
    <section className="py-24 lg:py-32" style={{ background: BG }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <FadeIn>
          <div className="text-center mb-16">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: GOLD, fontFamily: "'Outfit', sans-serif" }}>{t.label}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t.title1}<span style={{ color: GOLD }}>{t.title2}</span>
            </h2>
          </div>
        </FadeIn>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.items.map((c, i) => {
            const Icon = capIcons[i];
            return (
              <FadeIn key={i} delay={i * 0.1}>
                <div data-testid={`capability-${i}`} className="p-8 border border-white/5 hover:border-white/15 transition-all duration-500 text-center group" style={{ background: SURFACE }}>
                  <Icon size={32} strokeWidth={1.5} style={{ color: GOLD }} className="mx-auto mb-5 group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="text-lg font-medium text-white mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{c.label}</h3>
                  <p className="text-xs text-slate-500 tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>{c.desc}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ══════════════════ ABOUT ══════════════════
function About({ lang }) {
  const t = T[lang].about;
  return (
    <section id="nosotros" className="py-24 lg:py-32 border-y border-white/5" style={{ background: SURFACE }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: GOLD, fontFamily: "'Outfit', sans-serif" }}>{t.label}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t.title1}<br /><span style={{ color: GOLD }}>{t.title2}</span>
            </h2>
            <div className="space-y-5 text-slate-300 text-sm lg:text-base leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>
              <p>{t.p1}</p>
              <p>{t.p2}</p>
              <p>{t.p3}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1771270759486-1f7703945072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBleGVjdXRpdmUlMjBvZmZpY2UlMjBkYXJrJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NTM1MTgxNHww&ixlib=rb-4.1.0&q=85" alt="Midas office" className="w-full h-80 lg:h-96 object-cover opacity-70" />
              <div className="absolute inset-0 border border-white/10" />
              <div className="absolute bottom-0 left-0 right-0 p-8" style={{ background: `linear-gradient(to top, ${SURFACE}, transparent)` }}>
                <div className="grid grid-cols-2 gap-6">
                  {t.stats.map(([v, l], i) => (
                    <div key={i}>
                      <div className="text-2xl font-medium text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{v}</div>
                      <div className="text-[10px] tracking-[0.12em] uppercase text-slate-400 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

// ══════════════════ CONTACT ══════════════════
function Contact({ lang }) {
  const t = T[lang].contact;
  const [form, setForm] = useState({ nombre: "", email: "", empresa: "", mensaje: "" });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setTimeout(() => setSent(false), 4000);
    setForm({ nombre: "", email: "", empresa: "", mensaje: "" });
  };

  const inputStyle = {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "#fff",
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 300,
    fontSize: 14,
    padding: "14px 16px",
    width: "100%",
    outline: "none",
    transition: "border-color 0.3s"
  };

  return (
    <section id="contacto" className="py-24 lg:py-32 border-t border-white/5" style={{ background: SURFACE }}>
      <div className="max-w-3xl mx-auto px-6 lg:px-12">
        <FadeIn>
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-4" style={{ color: GOLD, fontFamily: "'Outfit', sans-serif" }}>{t.label}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t.title}</h2>
            <p className="text-sm text-slate-400" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}>{t.desc}</p>
          </div>
        </FadeIn>
        <FadeIn delay={0.15}>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <input data-testid="contact-nombre" type="text" placeholder={t.nombre} value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} required style={inputStyle} onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
              <input data-testid="contact-email" type="email" placeholder={t.email} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required style={inputStyle} onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
            </div>
            <input data-testid="contact-empresa" type="text" placeholder={t.empresa} value={form.empresa} onChange={e => setForm({ ...form, empresa: e.target.value })} style={inputStyle} onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
            <textarea data-testid="contact-mensaje" placeholder={t.mensaje} value={form.mensaje} onChange={e => setForm({ ...form, mensaje: e.target.value })} rows={5} required style={{ ...inputStyle, resize: "none" }} onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.1)"} />
            <button data-testid="contact-submit" type="submit" className="flex items-center gap-3 px-8 py-4 text-sm tracking-[0.15em] font-medium transition-all duration-300 hover:brightness-110 hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] w-full justify-center" style={{ background: GOLD, color: "#000", fontFamily: "'Outfit', sans-serif" }}>
              {sent ? t.sent : t.btn} <Send size={14} />
            </button>
          </form>
        </FadeIn>
      </div>
    </section>
  );
}

// ══════════════════ FOOTER ══════════════════
function Footer({ lang }) {
  return (
    <footer className="py-12 border-t border-white/5" style={{ background: BG }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 border border-[#D4AF37]/50 flex items-center justify-center">
            <span style={{ color: GOLD, fontSize: 10 }}>M</span>
          </div>
          <span className="text-sm tracking-[0.15em] text-slate-500" style={{ fontFamily: "'Cormorant Garamond', serif" }}>MIDAS</span>
        </div>
        <p className="text-xs text-slate-600 tracking-wide" style={{ fontFamily: "'Outfit', sans-serif" }}>
          &copy; {new Date().getFullYear()} Midas. {T[lang].footer}
        </p>
      </div>
    </footer>
  );
}

// ══════════════════ LANDING (MAIN) ══════════════════
export default function Landing({ onAccess }) {
  const [lang, setLang] = useState(() => localStorage.getItem("midas_lang") || "es");

  useEffect(() => {
    localStorage.setItem("midas_lang", lang);
  }, [lang]);

  return (
    <div style={{ background: BG, minHeight: "100vh" }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
      <Header onAccess={onAccess} lang={lang} setLang={setLang} />
      <Hero onAccess={onAccess} lang={lang} />
      <Marquee lang={lang} />
      <Services lang={lang} />
      <Capabilities lang={lang} />
      <About lang={lang} />
      <Contact lang={lang} />
      <Footer lang={lang} />
    </div>
  );
}
